<template>
  <Dialog :open="isOpen" @update:open="setIsOpen">
    <DialogContent class="sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>Share {{ resourceType || null }}</DialogTitle>
      </DialogHeader>

      <div class="mt-4">
        <span class="text-sm mb-1 block">Share via</span>

        <div class="text-sm text-gray-700 flex justify-between">
          <div data-testid="share-modal-facebook" class="flex pointer-events-auto cursor-pointer" @click="shareToSocial('facebook')">
            <div class="flex flex-col items-center">
              <Facebook :class="iconClasses" />
              <p :class="labelClasses">Facebook</p>
            </div>
          </div>
          <div data-testid="share-modal-twitter" class="flex pointer-events-auto cursor-pointer" @click="shareToSocial('twitter')">
            <div class="flex flex-col items-center">
              <X :class="iconClasses" />
              <p :class="labelClasses">X</p>
            </div>
          </div>
          <div data-testid="share-modal-linkedin" class="flex pointer-events-auto cursor-pointer" @click="shareToSocial('linkedin')">
            <div class="flex flex-col items-center">
              <LinkedIn :class="iconClasses" />
              <p :class="labelClasses">LinkedIn</p>
            </div>
          </div>
          <div
            v-if="props.entity"
            data-testid="share-modal-embed"
            class="flex pointer-events-auto cursor-pointer"
            @click="handleEmbedClick"
          >
            <div class="flex flex-col items-center">
              <CodeXml :class="iconClasses" />
              <p :class="labelClasses">Embed</p>
            </div>
          </div>
        </div>
      </div>

      <Separator label="Or" class="my-3" />

      <div class="flex flex-col text-gray-800">
        <p class="mb-2 text-sm">Copy Link</p>
        <div class="flex flex-row justify-center h-10">
          <Input class="text-sm w-full mr-4" type="text" readonly :model-value="url" />
          <TooltipProvider>
            <Tooltip :open="showCopyTooltip">
              <TooltipTrigger>
                <Button
                  data-testid="share-menu-copy-link-btn"
                  class="flex shrink-0 items-center justify-between gap-2"
                  @click.prevent="copyLink"
                >
                  <Copy />
                  Copy
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Copied!</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script lang="ts" setup>
import { CodeXml, Copy } from 'lucide-vue-next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import Facebook from '~/assets/icons/social/Facebook.vue'
import X from '~/assets/icons/social/X.vue'
import LinkedIn from '~/assets/icons/social/LinkedIn.vue'

export interface ShareModalState {
  isOpen: boolean
  title: string
  url: string
  entity?: any
  resourceType?: string
}

const props = defineProps<ShareModalState>()

const emit = defineEmits<{
  'update:isOpen': [value: boolean]
  embed: [entity: any]
}>()

const showCopyTooltip = ref(false)
const iconClasses = 'h-10 w-10 p-2 m-2 border border-gray-200 rounded-lg'
const labelClasses = 'mb-2 text-xs font-normal'

function setIsOpen(value: boolean) {
  emit('update:isOpen', value)
}

function shareToSocial(network: string): void {
  // Define popup dimensions
  const width = 550
  const height = 420
  const left = (window.screen.width - width) / 2
  const top = (window.screen.height - height) / 2
  const popupOptions = `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`

  // Create the appropriate URL based on the network
  let url = ''
  switch (network) {
    case 'facebook':
      url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(props.url)}&t=${encodeURIComponent(props.title)}`
      break
    case 'twitter':
      url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(props.url)}&text=${encodeURIComponent(props.title)}`
      break
    case 'linkedin':
      url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(props.url)}`
      break
  }

  window.open(url, `share_${network}`, popupOptions)
}

async function copyLink(): Promise<void> {
  await navigator.clipboard.writeText(props.url)
  showCopyTooltip.value = true

  setTimeout(() => {
    showCopyTooltip.value = false
  }, 2000)
}

async function handleEmbedClick(): Promise<void> {
  emit('embed', props.entity)
  setIsOpen(false)
}
</script>
